import Cookies, { type CookieSetOptions } from 'universal-cookie';
import { AUTHENTICATION_ACTIONS, telemetry } from 'utils/telemetry';
import { ADMIN, SUPER_ADMIN } from 'constants/roles';

const buildCookies = (ops) => {
  return new Cookies(null, {
    path: '/',
    ...ops,
  });
};

export const CookieUtils = {
  _cookies: null as null | Cookies,

  _getCookieContext: (ops = {}) => {
    if (!CookieUtils._cookies) {
      CookieUtils._cookies = buildCookies(ops);
    }
    return CookieUtils._cookies;
  },

  saveHeaders: (headers, uid) => {
    const { access_token, refresh_token, scopes, token_type } = headers;
    const cookies = CookieUtils._getCookieContext();

    cookies.set('admin_access_token', access_token);
    cookies.set('access_token', access_token);
    cookies.set('admin_refresh_token', refresh_token);
    cookies.set('refresh_token', refresh_token);
    cookies.set('admin_to_compass_redirect_refactor', true);
    cookies.set('scopes', scopes);
    cookies.set('token_type', token_type);
    cookies.set('uid', uid);

    telemetry.addAction(AUTHENTICATION_ACTIONS.AUTH_DATA_SET);
  },

  // Saves only the cookies Admin Portal reads. Unlike saveHeaders, it skips the Compass handoff
  // cookies (`access_token`, `refresh_token`, `admin_to_compass_redirect_refactor`) and saves no
  // refresh token: under Auth0 a 401 refreshes through the Auth0 SDK, never through Diglet.
  saveAuth0Headers: ({ access_token, scopes }: { access_token: string; scopes: string[] }, uid: string) => {
    const cookies = CookieUtils._getCookieContext();

    cookies.set('admin_access_token', access_token);
    cookies.set('scopes', scopes);
    cookies.set('token_type', 'Bearer');
    cookies.set('uid', uid);

    telemetry.addAction(AUTHENTICATION_ACTIONS.AUTH_DATA_SET);
  },

  get: (key) => {
    const cookies = CookieUtils._getCookieContext();
    return cookies.get(key);
  },

  getUserScopes: (): string[] => {
    const scopes = CookieUtils.get('scopes');
    if (scopes == null) return [];
    return Array.isArray(scopes) ? scopes : String(scopes).split(',');
  },

  isAdmin: () => CookieUtils.getUserScopes().includes(ADMIN),

  isSuperAdmin: () => CookieUtils.getUserScopes().includes(SUPER_ADMIN),

  getAuthorizationHeader: () => {
    const cookies = CookieUtils._getCookieContext();
    const access_token = cookies.get('admin_access_token');
    const token_type = cookies.get('token_type');

    return { Authorization: `${token_type} ${access_token}` };
  },

  updateTokenExpiration(access) {
    const cookies = CookieUtils._getCookieContext();
    cookies.set('access_token_expires_at', access);
  },

  updateHeaders: (headers) => {
    const cookies = CookieUtils._getCookieContext();

    const updatedHeaders = {
      ...headers,
      // If access_token or refresh_token are passed as headers, also set them with the `admin_` prefix
      ...(headers.access_token && { admin_access_token: headers.access_token }),
      ...(headers.refresh_token && { admin_refresh_token: headers.refresh_token }),
    };

    for (const header in updatedHeaders) {
      cookies.set(header, updatedHeaders[header]);
    }

    telemetry.addAction(AUTHENTICATION_ACTIONS.AUTH_DATA_UPDATED);
  },

  clearHeaders: () => {
    const cookies = CookieUtils._getCookieContext();

    cookies.remove('admin_access_token');
    cookies.remove('access_token');
    cookies.remove('admin_refresh_token');
    cookies.remove('refresh_token');
    cookies.remove('access_token_expires_at');
    cookies.remove('admin_to_compass_redirect_refactor');
    cookies.remove('token_type');
    cookies.remove('scopes');
    cookies.remove('uid');
    cookies.remove('uuid');

    telemetry.addAction(AUTHENTICATION_ACTIONS.AUTH_DATA_CLEARED);
  },

  canGetUserInfo: () => {
    const cookies = CookieUtils._getCookieContext();

    return !!(cookies.get('admin_access_token') && cookies.get('token_type') && cookies.get('admin_refresh_token'));
  },

  set: (key: string, value: unknown, options?: CookieSetOptions) => {
    const cookies = CookieUtils._getCookieContext();
    cookies.set(key, value, options);
  },

  remove: (key: string) => {
    const cookies = CookieUtils._getCookieContext();
    cookies.remove(key);
  },

  /**
   * Removes old cookies that were set with a domain option.
   * These cookies cannot be removed using the standard remove() method
   * because they require the same domain to be specified.
   */
  removeOldDomainCookies: () => {
    if (typeof window === 'undefined') return;

    const cookies = CookieUtils._getCookieContext();

    // List of cookies that might have been set with domain option
    const oldCookieNames = [
      'access_token',
      'refresh_token',
      'admin_access_token',
      'admin_refresh_token',
      'admin_to_compass_redirect_refactor',
      'scopes',
      'token_type',
      'uid',
      'uuid',
      'access_token_expires_at',
    ];

    // Based on the old logic: `.${window.location.host.split('.').slice(-2).join('.')}`, which
    // was used as the domain for production, internal, and demo.
    // E.g., delete cookies with a domain of .springhealth.com and springhealth.com, but not
    // admin.springhealth.com
    //
    // See https://github.com/SpringCare/admin-portal/pull/2126/files.
    const hostParts = window.location.host.split('.');

    const possibleOldDomains = hostParts.length >= 2 ? [`.${hostParts.slice(-2).join('.')}`] : [];

    oldCookieNames.forEach((cookieName) => {
      possibleOldDomains.forEach((oldDomain) => {
        cookies.remove(cookieName, { domain: oldDomain });
        const domainWithoutDot = oldDomain.startsWith('.') ? oldDomain.slice(1) : oldDomain;
        cookies.remove(cookieName, { domain: domainWithoutDot });

        telemetry.addAction(AUTHENTICATION_ACTIONS.OLD_DOMAIN_COOKIE_REMOVED, {
          domain: oldDomain,
        });
      });
    });
  },
};

export default CookieUtils;
